import { FormDatePicker, FormSelect } from "@/components";
import { Button } from "@/components/ui";
import { useUpdateOwnershipAccountMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, Pencil, Save, X } from "lucide-react";
import moment from "moment";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "@/components/Toast";
import { z } from "zod";

const fmtDate = (v?: string) => (v ? moment(v).format("MM/DD/YYYY") : "—");
const openEnded = (v?: string) => (v ? fmtDate(v) : "Open-ended");
const humanize = (v?: string) => (v ? v.replace(/_/g, " ") : "—");
const yesNo = (v?: boolean) => (v ? "Yes" : "No");
const todayIso = () => moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
const YES_NO = [{ label: "Yes", value: "YES" }, { label: "No", value: "NO" }];

// Editable groups: value field + its Effective From / To
const GROUPS = [
  { value: "ecommSignedUp", from: "ecommEffectiveFrom", to: "ecommEffectiveTo", label: "E-Communication" },
  { value: "legalAction", from: "legalActionEffectiveFrom", to: "legalActionEffectiveTo", label: "Legal action" },
  { value: "lienExercised", from: "lienEffectiveFrom", to: "lienEffectiveTo", label: "Lien" },
  { value: "primaryUserId", from: "primaryContactEffectiveFrom", to: "primaryContactEffectiveTo", label: "Primary contact" },
] as const;

const dateField = z.string().optional().nullable();
const schema = z
  .object({
    ecommSignedUp: z.enum(["YES", "NO"]),
    legalAction: z.enum(["YES", "NO"]),
    lienExercised: z.enum(["YES", "NO"]),
    primaryUserId: z.string().min(1, "Please select the primary contact."),
    ...Object.fromEntries(GROUPS.flatMap((g) => [[g.from, dateField], [g.to, dateField]])),
  })
  .superRefine((data: any, ctx) => {
    GROUPS.forEach((g) => {
      if (!data[g.from]) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Please select Effective From.", path: [g.from] });
      }
      if (data[g.from] && data[g.to] && moment(data[g.to]).isBefore(moment(data[g.from]), "day")) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${g.label} Effective To cannot be before Effective From.`, path: [g.to] });
      }
    });
  });

const Field = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <div className="text-sm text-gray-900 break-words mt-0.5">{value === undefined || value === null || value === "" ? "—" : value}</div>
  </div>
);

interface CurrentTitleSectionProps {
  title: any;            // active row from the Property Ownership grid
  account: any;          // GET /property-ownerships/{id}/account
  communityId: string;
  canEdit: boolean;
  onOpenTitle: () => void;
}

/** Current Title on the property detail page; edits the title's dated account fields. */
const CurrentTitleSection = ({ title, account, communityId, canEdit, onOpenTitle }: CurrentTitleSectionProps) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const form = useForm<any>({ resolver: zodResolver(schema) });
  const errors: any = form.formState.errors;

  const toForm = () => ({
    ecommSignedUp: account?.ecommSignedUp ? "YES" : "NO",
    ecommEffectiveFrom: account?.ecommEffectiveFrom || "",
    ecommEffectiveTo: account?.ecommEffectiveTo || "",
    legalAction: account?.legalAction ? "YES" : "NO",
    legalActionEffectiveFrom: account?.legalActionEffectiveFrom || "",
    legalActionEffectiveTo: account?.legalActionEffectiveTo || "",
    lienExercised: account?.lienExercised ? "YES" : "NO",
    lienEffectiveFrom: account?.lienEffectiveFrom || "",
    lienEffectiveTo: account?.lienEffectiveTo || "",
    primaryUserId: account?.primaryUserId ? String(account.primaryUserId) : "",
    primaryContactEffectiveFrom: account?.primaryContactEffectiveFrom || "",
    primaryContactEffectiveTo: account?.primaryContactEffectiveTo || "",
  });
  const original = useRef<any>({});
  useEffect(() => {
    if (account) {
      original.current = toForm();
      form.reset(original.current);
    }
  }, [account]);

  // A changed value starts a new period: From = today, To cleared (both still editable)
  useEffect(() => {
    const sub = form.watch((values: any, { name }) => {
      const group = GROUPS.find((g) => g.value === name);
      if (!group) return;
      if (values[group.value] !== original.current[group.value]) {
        form.setValue(group.from, todayIso());
        form.setValue(group.to, "");
      } else {
        form.setValue(group.from, original.current[group.from]);
        form.setValue(group.to, original.current[group.to]);
      }
    });
    return () => sub.unsubscribe();
  }, [form]);

  const { mutate: updateAccount, isPending: isSaving } = useUpdateOwnershipAccountMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : "Something went wrong"
      );
    },
    onSuccess: () => {
      toast.success("Current title updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["ownership-account", communityId, String(title.ownershipId)] });
      queryClient.invalidateQueries({ queryKey: ["ownership-grid"] });
      setIsEditing(false);
    },
  });

  const onSubmit = (v: any) => {
    const date = (x: string) => (x ? x : null);
    updateAccount({
      communityId,
      ownershipId: String(title.ownershipId),
      data: {
        ecommSignedUp: v.ecommSignedUp === "YES",
        ecommEffectiveFrom: date(v.ecommEffectiveFrom),
        ecommEffectiveTo: date(v.ecommEffectiveTo),
        legalAction: v.legalAction === "YES",
        legalActionEffectiveFrom: date(v.legalActionEffectiveFrom),
        legalActionEffectiveTo: date(v.legalActionEffectiveTo),
        lienExercised: v.lienExercised === "YES",
        lienEffectiveFrom: date(v.lienEffectiveFrom),
        lienEffectiveTo: date(v.lienEffectiveTo),
        primaryUserId: Number(v.primaryUserId),
        primaryContactEffectiveFrom: date(v.primaryContactEffectiveFrom),
        primaryContactEffectiveTo: date(v.primaryContactEffectiveTo),
      },
    });
  };

  const isJoint = account?.ownershipType === "JOINT";
  const ownerOptions = (account?.ownerOptions || []).map((o: any) => ({ label: o.name, value: String(o.userId) }));
  // Any active owner on the title can be made the primary contact (joint owners for JOINT titles)
  const canPickPrimary = ownerOptions.length > 0;
  const primaryContactView = isJoint && account?.jointOwners?.length
    ? <ul>{account.jointOwners.map((n: string) => <li key={n}>{n}{n === title.primaryContactName ? " (primary)" : ""}</li>)}</ul>
    : title.primaryContactName;

  // Rows of "value | Effective From | Effective To"
  const rows: { label: string; view: ReactNode; edit?: ReactNode; from: string; to: string; group?: string }[] = [
    { label: "Occupancy Type", view: humanize(title.occupancyType),
      from: title.occupancyType ? fmtDate(account?.occupancyEffectiveFrom) : "—",
      to: title.occupancyType ? openEnded(account?.occupancyEffectiveTo) : "—" },
    { label: isJoint ? "Primary Contact (joint owners)" : "Primary Contact", view: primaryContactView,
      edit: canPickPrimary
        ? <FormSelect name="primaryUserId" control={form.control} options={ownerOptions} error={errors.primaryUserId?.message} />
        : undefined,
      from: fmtDate(account?.primaryContactEffectiveFrom), to: openEnded(account?.primaryContactEffectiveTo), group: "primaryUserId" },
    { label: "E-Communication", view: yesNo(account?.ecommSignedUp),
      edit: <FormSelect name="ecommSignedUp" control={form.control} options={YES_NO} />,
      from: fmtDate(account?.ecommEffectiveFrom), to: openEnded(account?.ecommEffectiveTo), group: "ecommSignedUp" },
    { label: "Legal Action", view: yesNo(account?.legalAction),
      edit: <FormSelect name="legalAction" control={form.control} options={YES_NO} />,
      from: fmtDate(account?.legalActionEffectiveFrom), to: openEnded(account?.legalActionEffectiveTo), group: "legalAction" },
    { label: "Lien Exercised", view: yesNo(account?.lienExercised),
      edit: <FormSelect name="lienExercised" control={form.control} options={YES_NO} />,
      from: fmtDate(account?.lienEffectiveFrom), to: openEnded(account?.lienEffectiveTo), group: "lienExercised" },
  ];

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-primary flex items-center gap-2">
            <FileText className="w-4 h-4" />Current Title
          </h4>
          {isEditing ? (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" disabled={isSaving}
                onClick={() => { form.reset(original.current); setIsEditing(false); }}>
                <X className="w-4 h-4" /> Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isSaving}><Save className="w-4 h-4" /> Save</Button>
            </div>
          ) : canEdit && account ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              <Pencil className="w-4 h-4" /> Edit Title
            </Button>
          ) : <></>}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Property Ownership ID" value={
            <button type="button" className="text-primary font-semibold hover:underline" onClick={onOpenTitle}>{title.ownershipCode}</button>
          } />
          <Field label="Ownership Type" value={account?.ownershipType} />
          <Field label="Start Date" value={fmtDate(title.startDate)} />
          <Field label="End Date" value={openEnded(title.endDate)} />
          <Field label="Primary Phone" value={title.primaryPhone} />
          <Field label="Primary Email" value={title.primaryEmail} />
          <Field label="Owners on Title" value={account?.ownersOnTitle} />
          <Field label="Allocated Ownership" value={account ? `${Number(account.allocatedOwnership || 0)}%` : undefined} />
        </div>

        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-4 gap-y-3 bg-gray-50 rounded-lg p-4">
          {rows.map((r) => {
            const g = GROUPS.find((x) => x.value === r.group);
            const editable = isEditing && g;
            return (
              <Fragment key={r.label}>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{r.label}</p>
                  <div className="text-sm text-gray-900 mt-0.5">{isEditing && r.edit ? r.edit : r.view}</div>
                </div>
                {editable ? (
                  <FormDatePicker name={g.from} control={form.control} label="Effective From" isRequired error={errors[g.from]?.message} />
                ) : <Field label="Effective From" value={r.from} />}
                {editable ? (
                  <FormDatePicker name={g.to} control={form.control} label="Effective To" error={errors[g.to]?.message} />
                ) : <Field label="Effective To" value={r.to} />}
              </Fragment>
            );
          })}
        </div>
        {isEditing ? (
          <p className="text-xs text-gray-500">
            Occupancy is derived from active owners/tenants and can't be edited. Leave Effective To blank for open-ended.
            Changing E-Communication closes the previous period the day before the new Effective From.
          </p>
        ) : <></>}
      </form>
    </FormProvider>
  );
};

export default CurrentTitleSection;
